import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Input } from './input';
import { Button } from './button';

/**
 * Free-entry combobox: shows a filtered list of suggestions but lets the user
 * type/keep any value that isn't in the list. Controlled via value/onChange so
 * it plugs into react-hook-form's Controller.
 */
export function AutocompleteInput({
  value,
  onChange,
  onBlur,
  suggestions = [],
  placeholder,
  className,
  disabled = false,
  testId,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const query = value || '';
  const filtered = query
    ? suggestions.filter((s) => s.toLowerCase().includes(query.toLowerCase()))
    : suggestions;

  return (
    <div className="relative" ref={wrapRef}>
      <div className="relative">
        <Input
          value={query}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          placeholder={placeholder}
          className={className}
          disabled={disabled}
          autoComplete="off"
          data-testid={testId}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
          onClick={() => setOpen((o) => !o)}
          tabIndex={-1}
        >
          <ChevronDown
            className={`w-4 h-4 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </Button>
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {filtered.slice(0, 50).map((s, index) => (
            <div
              key={index}
              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${value === s ? 'bg-blue-50 text-blue-700' : ''}`}
              onClick={() => {
                onChange(s);
                setOpen(false);
              }}
              data-testid={testId ? `${testId}-option-${index}` : undefined}
            >
              {s}
            </div>
          ))}
          {filtered.length > 50 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t">
              Showing first 50 results. Type to filter more...
            </div>
          )}
        </div>
      )}
    </div>
  );
}
